import { memo, useCallback } from 'react';
import { Flex, Switch, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { getPlannerMode, handlePlannerMode } from 'redux/PlannerModeSlice';

import {
  PLAN_BUILDER_MODE_BG,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  accent,
  controlHeight,
  primaryAction,
  text as textToken,
} from '../planPageRevampUi';
import PlanWrenchIcon from '../icons/PlanWrenchIcon';

/**
 * The plan page's own Builder mode pill.
 *
 * `BuilderModeV1/components/BuilderModeToggle` renders the same control, but on
 * that module's constants — 36px tall, 10px radius — and it is shared with
 * BuilderModeV1, so restyling it there would drag an unrelated screen along.
 * This copy is identical in behaviour (same `handlePlannerMode` action, same
 * `getPlannerMode` state, same icon) and differs only in sitting on the shared
 * revamp tokens, so it lines up optically with the scenario/date group beside
 * it instead of standing 4px taller than everything else in the row.
 */
const PlanBuilderModeToggle = memo(() => {
  const dispatch = useDispatch();
  const isBuilderModeOn = useSelector(getPlannerMode);

  const handleToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(handlePlannerMode(event.target.checked));
    },
    [dispatch],
  );

  return (
    <Flex
      as='label'
      align='center'
      gap={2}
      px={3}
      h={controlHeight}
      minH={controlHeight}
      bg={PLAN_BUILDER_MODE_BG}
      borderRadius={PLAN_REVAMP_RADIUS_SM}
      cursor='pointer'
      flexShrink={0}
      boxSizing='border-box'
      transition='background 0.12s ease'
      _hover={{ bg: primaryAction.hover }}
    >
      <PlanWrenchIcon width={14} height={14} color={textToken.inverse} />
      {/* 13px/500, matching the date control beside it (and DimensionsRevamp's
          header buttons). It was 14px/400, so two controls of identical height
          sitting side by side carried visibly different text. */}
      <Text {...PLAN_TEXT.toolbarTab} color={textToken.inverse} whiteSpace='nowrap'>
        Builder mode
      </Text>
      {/* Geometry comes from Chakra's own switch variables rather than fixed
          w/h plus a hand-written `translateX`: Chakra derives the thumb size
          and the checked travel from these three values, so overriding only the
          rendered width/height (as this did) leaves the travel maths on the
          size defaults and the knob stops short of the track's end.
          `size='sm'` stays because it is the base these vars refine — dropping
          it fell back to `md` (30×16), which is what made the toggle bulge.
          22 × 12 at 1px padding gives a 10px knob and a 10px travel, scaled to
          the 32px pill and 13px label rather than the 36px pill this control
          was originally drawn for. */}
      <Switch
        size='sm'
        isChecked={isBuilderModeOn}
        onChange={handleToggle}
        sx={{
          '--switch-track-width': '22px',
          '--switch-track-height': '12px',
          '--switch-track-padding': '1px',
          '& .chakra-switch__track': {
            bg: textToken.inverse,
            _checked: { bg: textToken.inverse },
          },
          '& .chakra-switch__thumb': {
            // Off reads as chrome (the dark pill showing through), on reads as
            // accent — the one place accent belongs in this header.
            bg: isBuilderModeOn ? accent.solid : PLAN_BUILDER_MODE_BG,
            boxShadow: '0px 1.459px 3.405px rgba(0, 0, 0, 0.12)',
          },
        }}
      />
    </Flex>
  );
});

PlanBuilderModeToggle.displayName = 'PlanBuilderModeToggle';

export default PlanBuilderModeToggle;
